/**
 * useApiQuery Hook
 *
 * Type-safe React Query hook for GET requests.
 * Supports both single resource fetching and list fetching with pagination.
 */

import { type Query, useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import type { paths } from '@access-cms/api-types';
import { HttpMethod, type QueryRefetchIntervals } from '@access-cms/shared-constants';

import { ApiRequestError, get, post } from '../client';
import type { QueryKeyBase } from '../queryKeys';
import type { ApiPaths, PathParams, QueryParams, RequestBody, SuccessResponse } from '../types';

// =============================================================================
// Type Utilities
// =============================================================================

/**
 * Paths that have a GET method
 */
type GetPaths = {
  [P in ApiPaths]: paths[P] extends { get: object } ? P : never;
}[ApiPaths];

/**
 * Extract the response type for a GET path
 */
type GetResponse<P extends GetPaths> = SuccessResponse<P, 'get'>;

/**
 * Extract the data type from a response (unwraps { data: T })
 */
type GetData<P extends GetPaths> = GetResponse<P> extends { data?: infer D } ? D : GetResponse<P>;

/**
 * For setting React Query `refetchInterval`.
 * Can be one of:
 * - A predefined {@link QueryRefetchIntervals} value
 * - Set to `false` to completely disable refetching
 * - A function that returns either from the latest query state.
 */
export type RefetchInterval<TData, TError = ApiRequestError> =
  | QueryRefetchIntervals
  | false
  | ((query: Query<TData, TError>) => QueryRefetchIntervals | false);

// =============================================================================
// Hook Options Types
// =============================================================================

interface UseApiQueryOptions<P extends GetPaths, TData = GetData<P>> {
  /**
   * Additional React Query options
   */
  options?: Omit<UseQueryOptions<GetResponse<P>, ApiRequestError, TData>, 'queryKey' | 'queryFn' | 'select'>;

  /**
   * The API path to query
   */
  path: P;

  /**
   * Path parameters (e.g., { id: '123' } for '/api/v1/users/{id}')
   */
  pathParams?: PathParams<P, 'get'>;

  /**
   * Query key for caching. If not provided, uses [path, pathParams, queryParams]
   */
  queryKey?: QueryKeyBase;

  /**
   * Query parameters (e.g., { page: 1, limit: 20 })
   */
  queryParams?: QueryParams<P, 'get'>;

  /**
   * Transform the response data
   */
  select?: (data: GetResponse<P>) => TData;
}

// =============================================================================
// Main Hook
// =============================================================================

/**
 * Type-safe hook for making GET requests with React Query
 *
 * @example
 * ```tsx
 * // Fetch a single user
 * const { data: user, isLoading } = useApiQuery({
 *   path: '/api/v1/users/{id}',
 *   pathParams: { id: userId },
 *   queryKey: queryKeys.users.detail(userId),
 * });
 *
 * // Fetch list of users with pagination
 * const { data: users } = useApiQuery({
 *   path: '/api/v1/users',
 *   queryParams: { page: 1, limit: 20, status: 'ACTIVE' },
 *   queryKey: queryKeys.users.list({ page: 1, limit: 20, status: 'ACTIVE' }),
 * });
 *
 * // With custom select to transform data
 * const { data: userEmails } = useApiQuery({
 *   path: '/api/v1/users',
 *   queryKey: queryKeys.users.list(),
 *   select: (response) => response.data?.map(u => u.email) ?? [],
 * });
 * ```
 */
export function useApiQuery<P extends GetPaths, TData = GetData<P>>(
  config: UseApiQueryOptions<P, TData>,
): UseQueryResult<TData, ApiRequestError> {
  const { path, pathParams, queryParams, queryKey, select, options } = config;

  // Default query key if not provided - filter out undefined values
  const key = queryKey ?? ([path, pathParams, queryParams].filter(Boolean) as QueryKeyBase);

  return useQuery<GetResponse<P>, ApiRequestError, TData>({
    queryFn: async ({ signal }) => {
      return get(path, {
        pathParams,
        queryParams,
        signal,
      });
    },
    queryKey: key,
    select,
    ...options,
  });
}

// =============================================================================
// Convenience Hooks
// =============================================================================

/**
 * Hook for fetching a single resource by ID
 * Automatically extracts the data field from the response
 */
export function useApiGet<P extends GetPaths>(
  path: P,
  pathParams: PathParams<P, 'get'>,
  options?: {
    enabled?: boolean;
    gcTime?: number;
    queryKey?: QueryKeyBase;
    queryParams?: QueryParams<P, 'get'>;
    /** Poll on an interval while a caller is waiting on state it cannot be notified of. */
    refetchInterval?: number | false;
    staleTime?: number;
  },
): UseQueryResult<GetData<P>, ApiRequestError> {
  const key = options?.queryKey ?? ([path, pathParams, options?.queryParams].filter(Boolean) as QueryKeyBase);

  return useQuery<GetResponse<P>, ApiRequestError, GetData<P>>({
    enabled: options?.enabled,
    gcTime: options?.gcTime,
    queryFn: async ({ signal }) => {
      return get(path, {
        pathParams,
        queryParams: options?.queryParams,
        signal,
      });
    },
    queryKey: key,
    refetchInterval: options?.refetchInterval ?? false,
    select: (response) => (response as { data?: GetData<P> }).data as GetData<P>,
    staleTime: options?.staleTime,
  });
}

/**
 * Hook for fetching a list of resources
 * Returns the full response including pagination metadata
 */
export function useApiList<P extends GetPaths>(
  path: P,
  options?: {
    enabled?: boolean;
    gcTime?: number;
    pathParams?: PathParams<P, 'get'>;
    queryKey?: QueryKeyBase;
    queryParams?: QueryParams<P, 'get'>;
    refetchInterval?: RefetchInterval<GetResponse<P>, ApiRequestError>;
    staleTime?: number;
  },
): UseQueryResult<GetResponse<P>, ApiRequestError> {
  const key =
    options?.queryKey ??
    ([path, HttpMethod.GET, options?.pathParams, options?.queryParams].filter(Boolean) as QueryKeyBase);

  return useQuery<GetResponse<P>, ApiRequestError>({
    enabled: options?.enabled,
    gcTime: options?.gcTime,
    queryFn: async ({ signal }) => {
      return get(path, {
        pathParams: options?.pathParams,
        queryParams: options?.queryParams,
        signal,
      });
    },
    queryKey: key,
    refetchInterval: options?.refetchInterval,
    staleTime: options?.staleTime,
  });
}

// =============================================================================
// POST-based Queries
// =============================================================================

/**
 * Paths that have a POST method
 */
type PostPaths = {
  [P in ApiPaths]: paths[P] extends { post: object } ? P : never;
}[ApiPaths];

/**
 * Extract the response type for a POST path
 */
type PostResponse<P extends PostPaths> = SuccessResponse<P, 'post'>;

/**
 * Hook for POST-based search/list queries where filters live in the request
 * body rather than query params. Returns the full response including
 * pagination metadata.
 */
export function useApiSearch<P extends PostPaths>(
  path: P,
  body: RequestBody<P, 'post'>,
  options?: {
    enabled?: boolean;
    gcTime?: number;
    queryKey?: QueryKeyBase;
    staleTime?: number;
  },
): UseQueryResult<PostResponse<P>, ApiRequestError> {
  const key = options?.queryKey ?? ([path, HttpMethod.POST, body].filter(Boolean) as QueryKeyBase);

  return useQuery<PostResponse<P>, ApiRequestError>({
    enabled: options?.enabled,
    gcTime: options?.gcTime,
    queryFn: async ({ signal }) => {
      return post(path, body, { signal });
    },
    queryKey: key,
    staleTime: options?.staleTime,
  });
}
